/**
 * Benefícios extra do simulador VIP — regras, preços e desbloqueios por budget.
 * Textos visíveis são `{ pt, en }` (resolver com `l()` / `localize()`).
 */
import { localize, translate } from "@/i18n/I18nProvider";

/** @typedef {'toggle' | 'backpack' | 'recycler' | 'info'} BenefitKind */

/**
 * @typedef {Object} BackpackOption
 * @property {number} slots
 * @property {number} price
 * @property {string} label
 */

/** Patamares de budget de simulação (UI + desbloqueios). */
export const KIT_BONUS_MIN_BUDGET = 40;

/** Benefícios novos em cada patamar (a lista na UI é cumulativa). */
const TIER_EXCLUSIVE_UNLOCKS = {
    20: [
        { pt: "Farme automático (barris, minérios, árvores)", en: "Auto farm (barrels, ores, trees)" },
        { pt: "Sistema XP e Coins VIP [BETA]", en: "VIP XP & Coins system [BETA]" },
        { pt: "Mochila pequena no kit", en: "Small Backpack in the kit" },
    ],
    40: [
        { pt: "Kit Bónus (inventário fixo) — grátis", en: "Bonus Kit (fixed inventory) — free" },
        { pt: "Sentinelas e anti-aérea sem energia", en: "Turrets and SAM sites without power" },
        { pt: "Mochila grande no kit sem custo extra", en: "Large Backpack in the kit at no extra cost" },
        {
            pt: "Acesso ZaroAI e funcionalidades antecipadas (premium)",
            en: "ZaroAI access and early features (premium)",
        },
    ],
    60: [{ pt: "Recicladora na base ou via comando", en: "Recycler at base or via command" }],
    80: [],
};

const BUDGET_TIER_AMOUNTS = [20, 40, 60, 80];

function buildBudgetTiers() {
    const tiers = [];
    let cumulative = [];
    for (const amount of BUDGET_TIER_AMOUNTS) {
        const exclusive = TIER_EXCLUSIVE_UNLOCKS[amount] ?? [];
        cumulative = [...cumulative, ...exclusive];
        tiers.push({ amount, exclusiveUnlocks: exclusive, unlocks: [...cumulative] });
    }
    return tiers;
}

export const BUDGET_TIERS = buildBudgetTiers();

/** Patamar imediatamente inferior (null no primeiro). */
export function getPreviousBudgetTier(amount) {
    const idx = BUDGET_TIER_AMOUNTS.indexOf(Number(amount));
    if (idx <= 0) return null;
    return BUDGET_TIER_AMOUNTS[idx - 1];
}

export const PREMIUM_MIN_BUDGET = 40;

/** Menor patamar ≥ valor (para o budget seguir o total do pedido). */
export function minTierForAmount(amount) {
    const n = Number(amount) || 0;
    for (const tier of BUDGET_TIERS) {
        if (n <= tier.amount) return tier.amount;
    }
    return BUDGET_TIERS[BUDGET_TIERS.length - 1].amount;
}

/**
 * Mochilas nativas do Rust (sem plugin de backpack): o valor guardado em `backpackSlots` identifica
 * a mochila — 12 = Small Backpack (smallbackpack), 28 = Large Backpack (largebackpack).
 * Não mostrar nº de slots na UI.
 */
export const DEFAULT_BACKPACK_SLOTS = 12;
export const LARGE_BACKPACK_SLOTS = 28;

export const BACKPACK_OPTIONS = [
    { slots: DEFAULT_BACKPACK_SLOTS, price: 2, label: { pt: "Mochila pequena", en: "Small Backpack" } },
    { slots: LARGE_BACKPACK_SLOTS, price: 6, label: { pt: "Mochila grande", en: "Large Backpack" } },
];

/** Rascunhos antigos guardavam 18…144 espaços (plugin) → passam a mochila grande. */
export function normalizeBackpackSlots(value) {
    return Number(value) > DEFAULT_BACKPACK_SLOTS ? LARGE_BACKPACK_SLOTS : DEFAULT_BACKPACK_SLOTS;
}

/**
 * Mochila grátis por patamar de budget (simulação).
 * «Passar dos X» = budget ≥ X (inclui o valor no slider: 40, 60, 80…).
 */
const BACKPACK_FREE_BY_BUDGET = [
    { minBudget: 40, slots: [LARGE_BACKPACK_SLOTS] },
    { minBudget: 20, slots: [DEFAULT_BACKPACK_SLOTS] },
];

export const RECYCLER_MODES = [
    { id: "base", label: { pt: "Recicladora na base", en: "Recycler at base" } },
    { id: "command", label: { pt: "Recicladora via comando", en: "Recycler via command" } },
];

/**
 * @typedef {Object} BenefitDefinition
 * @property {string} id
 * @property {BenefitKind} kind
 * @property {{ pt: string, en: string }} title
 * @property {{ pt: string, en: string }} [description]
 * @property {boolean} [premium]
 * @property {number} [minBudget] — mínimo para activar (toggle/recycler)
 * @property {boolean} [free] — sem custo extra
 * @property {boolean} [defaultOn]
 */

/** @type {BenefitDefinition[]} */
export const BENEFIT_DEFINITIONS = [
    {
        id: "b_kit",
        kind: "info",
        title: { pt: "Kit Bónus", en: "Bonus Kit" },
        description: {
            pt: "Passa o rato no nome para ver o inventário incluído.",
            en: "Hover over the name to see the included inventory.",
        },
        minBudget: KIT_BONUS_MIN_BUDGET,
    },
    {
        id: "b_bp",
        kind: "backpack",
        title: { pt: "Mochila no kit", en: "Backpack in the kit" },
        description: {
            pt: "Mochila nativa do Rust incluída no kit: a pequena vem no patamar R$ 20; a grande tem custo extra até ficar grátis a partir do patamar R$ 40.",
            en: "Native Rust backpack included in the kit: the Small Backpack comes with the R$ 20 tier; the Large Backpack costs extra until it's free from the R$ 40 tier.",
        },
        defaultOn: true,
    },
    {
        id: "b_farm",
        kind: "toggle",
        title: { pt: "Farme automático", en: "Auto farm" },
        description: { pt: "Inclui barris, minérios e árvores.", en: "Includes barrels, ores and trees." },
        free: true,
    },
    {
        id: "b_sent",
        kind: "toggle",
        title: { pt: "Sentinelas e anti-aérea sem energia", en: "Turrets and SAM sites without power" },
        description: {
            pt: "Disponível com budget de simulação acima de R$ 40.",
            en: "Available with a simulation budget above R$ 40.",
        },
        minBudget: 40,
    },
    {
        id: "b_rec",
        kind: "recycler",
        title: { pt: "Recicladora", en: "Recycler" },
        description: {
            pt: "Escolhe instalação na base ou acesso por comando (após R$ 50 de budget).",
            en: "Choose a recycler placed at your base or access via command (from a R$ 50 budget).",
        },
        minBudget: 50,
    },
    {
        id: "b_xp",
        kind: "toggle",
        title: {
            pt: "Sistema exclusivo de XP e Coins para VIPs [BETA]",
            en: "Exclusive VIP XP & Coins system [BETA]",
        },
        free: true,
        defaultOn: true,
    },
    {
        id: "b_ai",
        kind: "toggle",
        title: { pt: "Acesso ao ZaroAI no Discord", en: "ZaroAI access on Discord" },
        premium: true,
    },
    {
        id: "b_early",
        kind: "toggle",
        title: { pt: "Acesso antecipado a novas funcionalidades", en: "Early access to new features" },
        premium: true,
    },
];

export function isBackpackSlotFreeAtBudget(slots, budget) {
    const n = Number(slots);
    const b = Number(budget);
    if (!Number.isFinite(n) || !Number.isFinite(b)) return false;
    for (const tier of BACKPACK_FREE_BY_BUDGET) {
        if (b >= tier.minBudget && tier.slots.includes(n)) return true;
    }
    return false;
}

/**
 * @param {number} slots
 * @param {number} budget
 */
export function effectiveBackpackPrice(slots, budget) {
    const n = normalizeBackpackSlots(slots);
    const opt = BACKPACK_OPTIONS.find((o) => o.slots === n);
    if (!opt) return 0;
    if (opt.price === 0) return 0;
    if (isBackpackSlotFreeAtBudget(n, budget)) return 0;
    return opt.price;
}

export function formatBackpackOptionLabel(option, budget, lang) {
    const effective = effectiveBackpackPrice(option.slots, budget);
    const label = localize(option.label, lang);
    if (option.slots === DEFAULT_BACKPACK_SLOTS && effective === 0) {
        return translate("benefits.backpack.optIncluded", { label }, lang);
    }
    if (effective === 0) {
        const from = backpackFreeThreshold(option.slots);
        return translate("benefits.backpack.optFree", { label, from }, lang);
    }
    return translate("benefits.backpack.optPrice", { label, price: effective }, lang);
}

function backpackFreeThreshold(slots) {
    for (const tier of BACKPACK_FREE_BY_BUDGET) {
        if (tier.slots.includes(slots)) return tier.minBudget;
    }
    return 0;
}

export function isBenefitUnlocked(def, budget, lockedPremium) {
    if (def.premium && lockedPremium) return false;
    if (def.minBudget != null && budget < def.minBudget) return false;
    return true;
}

/**
 * Benefícios que passam a estar disponíveis no patamar `tierBudget` mas não no `orderTotal` actual.
 * Devolve os títulos `{ pt, en }` (resolver com `l()` na UI).
 * @param {number} tierBudget
 * @param {number} orderTotal — kit + extras já calculados ao total actual
 */
export function getBenefitsUnlockedByHigherBudget(tierBudget, orderTotal) {
    const lockedPremium = tierBudget < PREMIUM_MIN_BUDGET;
    const out = [];
    for (const def of BENEFIT_DEFINITIONS) {
        if ((def.kind === "info" && def.minBudget == null) || (def.free && def.minBudget == null)) continue;
        if (!isBenefitUnlocked(def, tierBudget, lockedPremium)) continue;
        if (isBenefitUnlocked(def, orderTotal, orderTotal < PREMIUM_MIN_BUDGET)) continue;
        out.push(def.title);
    }
    return out;
}

/**
 * @param {number} tierBudget
 * @param {number} orderTotal
 */
export function buildBudgetGapMessage(tierBudget, orderTotal) {
    const gap = Math.round((tierBudget - orderTotal) * 100) / 100;
    if (gap <= 0) return null;
    const benefits = getBenefitsUnlockedByHigherBudget(tierBudget, orderTotal);
    const tier = BUDGET_TIERS.find((t) => t.amount === tierBudget);
    const prevTier = tier ? getPreviousBudgetTier(tier.amount) : null;
    return {
        gap,
        tierBudget,
        orderTotal,
        benefits,
        tierUnlocks: tier?.exclusiveUnlocks ?? [],
        tierInheritsFrom: prevTier,
    };
}

export function createInitialBenefitState() {
    return BENEFIT_DEFINITIONS.reduce((acc, def) => {
        const infoAlwaysOn = def.kind === "info" && def.minBudget == null;
        acc[def.id] = {
            enabled: def.defaultOn ?? infoAlwaysOn,
            backpackSlots: def.kind === "backpack" ? DEFAULT_BACKPACK_SLOTS : undefined,
            recyclerMode: def.kind === "recycler" ? "base" : undefined,
        };
        return acc;
    }, /** @type {Record<string, { enabled: boolean, backpackSlots?: number, recyclerMode?: string }>} */ ({}));
}

/** Activa benefícios grátis/incluídos quando o patamar os desbloqueia. */
export function applyBudgetUnlocks(benefitState, budget, lockedPremium) {
    const next = { ...benefitState };
    for (const def of BENEFIT_DEFINITIONS) {
        if (!isBenefitUnlocked(def, budget, lockedPremium)) {
            next[def.id] = { ...next[def.id], enabled: false };
            continue;
        }
        if (def.id === "b_kit" || def.free || def.defaultOn) {
            next[def.id] = { ...next[def.id], enabled: true };
        }
    }
    const bp = next.b_bp;
    if (bp && Number(bp.backpackSlots) !== normalizeBackpackSlots(bp.backpackSlots)) {
        next.b_bp = { ...bp, backpackSlots: normalizeBackpackSlots(bp.backpackSlots) };
    }
    return next;
}

/**
 * @param {typeof BENEFIT_DEFINITIONS[number]} def
 * @param {{ enabled: boolean, backpackSlots?: number, recyclerMode?: string }} state
 * @param {number} budget
 */
export function benefitSummaryLine(def, state, budget, lockedPremium = budget < PREMIUM_MIN_BUDGET, lang) {
    if (def.kind === "info" && def.id === "b_kit") {
        if (!state.enabled || !isBenefitUnlocked(def, budget, lockedPremium)) return null;
        return translate("benefits.summary.kit", null, lang);
    }
    if (def.kind === "backpack" && state.enabled) {
        const slots = normalizeBackpackSlots(state.backpackSlots ?? DEFAULT_BACKPACK_SLOTS);
        const price = effectiveBackpackPrice(slots, budget);
        const label = localize(BACKPACK_OPTIONS.find((o) => o.slots === slots)?.label, lang);
        const freeNote =
            price === 0 && slots > DEFAULT_BACKPACK_SLOTS
                ? translate("benefits.summary.backpackFree", null, lang)
                : "";
        const priceNote =
            price > 0
                ? translate("benefits.summary.backpackPrice", { price }, lang)
                : slots === DEFAULT_BACKPACK_SLOTS
                  ? ""
                  : freeNote;
        return translate("benefits.summary.backpack", { label, note: priceNote }, lang);
    }
    if (def.kind === "recycler" && state.enabled) {
        const modeLabel = RECYCLER_MODES.find((m) => m.id === state.recyclerMode)?.label;
        const mode = modeLabel
            ? localize(modeLabel, lang)
            : translate("benefits.summary.recyclerFallback", null, lang);
        return `♻️ ${mode}`;
    }
    if (!state.enabled) return null;
    const emoji =
        def.id === "b_farm"
            ? "⛏️"
            : def.id === "b_sent"
              ? "🛡️"
              : def.id === "b_xp"
                ? "🪙"
                : def.id === "b_ai"
                  ? "🤖"
                  : def.id === "b_early"
                    ? "🚀"
                    : "";
    return `${emoji} ${localize(def.title, lang)}`.trim();
}

/**
 * @param {Record<string, { enabled: boolean, backpackSlots?: number, recyclerMode?: string }>} benefitState
 * @param {number} budget
 */
export function computeBenefitsExtraCost(benefitState, budget) {
    let total = 0;
    const bp = benefitState.b_bp;
    if (bp?.enabled) {
        total += effectiveBackpackPrice(bp.backpackSlots ?? DEFAULT_BACKPACK_SLOTS, budget);
    }
    return total;
}

/**
 * @param {Record<string, { enabled: boolean, backpackSlots?: number, recyclerMode?: string }>} benefitState
 * @param {number} budget
 * @param {"pt"|"en"} [lang] — idioma dos textos (por omissão o atual)
 * @returns {{ id: string, text: string }[]}
 */
export function buildBenefitsSummaryList(benefitState, budget, lang) {
    const lockedPremium = budget < PREMIUM_MIN_BUDGET;
    const out = [];
    for (const def of BENEFIT_DEFINITIONS) {
        const state = benefitState[def.id];
        if (!state) continue;
        if (def.kind === "info") {
            const line = benefitSummaryLine(def, state, budget, lockedPremium, lang);
            if (line) out.push({ id: def.id, text: line });
            continue;
        }
        if (def.kind === "backpack") {
            const line = benefitSummaryLine(def, state, budget, undefined, lang);
            if (line) out.push({ id: def.id, text: line });
            continue;
        }
        if (def.kind === "recycler" || def.kind === "toggle") {
            if (!state.enabled) continue;
            const line = benefitSummaryLine(def, state, budget, undefined, lang);
            if (line) out.push({ id: def.id, text: line });
        }
    }
    return out;
}
